// YNX — PUBLIC LANDING PAGE — multilingual (5 languages)
// ───────────────────────────────────────────────────────────────
// Acquisition funnel positioning YNX as "AI OS for businesses worldwide".
// Hero · Features · Pricing (incl. add-ons) · FAQ · CTA → BusinessOnboarding.
// Languages: FR (default) · EN · ES · DE · IT — auto-detected from browser.
// ───────────────────────────────────────────────────────────────
import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowRight, Check, CalendarDays, KanbanSquare, ReceiptText,
  Users, PhoneCall, Image as ImageIcon, ShieldCheck, Zap, Globe,
  Sparkles, ChevronDown, Building2, UserCog, Bot, Plus,
} from 'lucide-react';
import { DICT, LANGS, detectLang, setLang as persistLang } from './landing_i18n';
import { ConsentBanner } from './ConsentBanner';
import { PreviewBanner } from './PreviewBanner';
import { track, trackPageView } from '../lib/tracker';
import './landing.css';

const FEATURE_ICONS = [
  CalendarDays, KanbanSquare, ReceiptText, Users, PhoneCall,
  ImageIcon, Bot, UserCog, ShieldCheck,
];

export const LandingPage = ({ onStart, onJoin }) => {
  const [lang, setLang] = useState(() => detectLang());
  const [openFaq, setOpenFaq] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const t = useMemo(() => DICT[lang] || DICT.fr, [lang]);

  // FIX (2 juil 2026) — Chrome shows a "translate this page?" prompt in a loop
  // when the rendered content language (auto-detected, e.g. EN) differs from
  // the static <html lang="fr">. Worse: if the user accepts, Google Translate
  // mutates React's DOM and crashes the app. Keep <html lang> in sync with the
  // ACTUAL displayed language, restore 'fr' when leaving (hub is French-only).
  useEffect(() => {
    document.documentElement.lang = lang;
    return () => { document.documentElement.lang = 'fr'; };
  }, [lang]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    // Track the page view once (fail-silent if consent not given yet)
    trackPageView({ source: 'landing' });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // CRITICAL FIX (30 juin 2026) — index.css locks <body> with
  // `overflow:hidden; height:100%` for the dashboard. Without this effect,
  // the public landing page CANNOT scroll on mobile (page caps at proof
  // banner, no scrollbar, user is stuck). We unlock the page-level scroll
  // ONLY while the landing page is mounted, and restore the dashboard's
  // scroll-lock when the user enters the business hub.
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');
    const prev = {
      htmlH: html.style.height,
      bodyH: body.style.height,
      bodyOvX: body.style.overflowX,
      bodyOvY: body.style.overflowY,
      rootH: root ? root.style.height : '',
      rootOv: root ? root.style.overflow : '',
    };
    html.style.height = 'auto';
    body.style.height = 'auto';
    body.style.overflowX = 'visible';
    body.style.overflowY = 'auto';
    if (root) {
      root.style.height = 'auto';
      root.style.overflow = 'visible';
    }
    return () => {
      html.style.height = prev.htmlH;
      body.style.height = prev.bodyH;
      body.style.overflowX = prev.bodyOvX;
      body.style.overflowY = prev.bodyOvY;
      if (root) {
        root.style.height = prev.rootH;
        root.style.overflow = prev.rootOv;
      }
    };
  }, []);

  const switchLang = (code) => {
    setLang(code);
    persistLang(code);
    setLangMenuOpen(false);
    track('lang_switch', { lang: code });
  };

  const trackedStart = (source) => {
    track('cta_click', { cta: 'start', source });
    if (onStart) onStart();
  };
  const trackedJoin = (source) => {
    track('cta_click', { cta: 'join', source });
    if (onJoin) onJoin();
  };

  const currentLang = LANGS.find((l) => l.code === lang) || LANGS[0];

  return (
    <div className="ynx-landing" lang={lang}>
      {/* Visible only when the master admin clicked "Aperçu visiteur" */}
      <PreviewBanner />
      {/* ─── NAV ─── */}
      <nav className={`ynx-nav ${scrolled ? 'scrolled' : ''}`} data-testid="landing-nav">
        <div className="ynx-nav-inner">
          <div className="ynx-nav-brand">
            <span className="ynx-nav-logo">YNX</span>
            <span className="ynx-nav-sub">by Carl TACITA</span>
          </div>
          <div className="ynx-nav-links">
            <a href="#features" className="ynx-nav-link">{t.nav.features}</a>
            <a href="#pricing" className="ynx-nav-link">{t.nav.pricing}</a>
            <a href="#faq" className="ynx-nav-link">{t.nav.faq}</a>

            {/* Language switcher */}
            <div className="ynx-lang-wrap">
              <button
                className="ynx-lang-btn"
                onClick={() => setLangMenuOpen((v) => !v)}
                data-testid="lang-switch-btn"
                aria-haspopup="listbox"
                aria-expanded={langMenuOpen}
              >
                <Globe size={13} />
                <span>{currentLang.flag} {currentLang.code.toUpperCase()}</span>
                <ChevronDown size={11} />
              </button>
              {langMenuOpen && (
                <div className="ynx-lang-menu" role="listbox">
                  {LANGS.map((l) => (
                    <button
                      key={l.code}
                      role="option"
                      aria-selected={l.code === lang}
                      className={`ynx-lang-item ${l.code === lang ? 'active' : ''}`}
                      onClick={() => switchLang(l.code)}
                      data-testid={`lang-${l.code}`}
                    >
                      <span>{l.flag}</span>
                      <span>{l.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button className="ynx-nav-ghost" onClick={() => trackedJoin("nav")} data-testid="landing-login-btn">
              {t.nav.login}
            </button>
            <button className="ynx-nav-cta" onClick={() => trackedStart("nav")} data-testid="landing-cta-nav">
              {t.nav.start} <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </nav>

      {/* ─── HERO ─── */}
      <header className="ynx-hero" data-testid="landing-hero">
        <div className="ynx-hero-bg">
          <div className="ynx-hero-grid"></div>
          <div className="ynx-hero-glow"></div>
        </div>
        <div className="ynx-hero-inner">
          <div className="ynx-hero-badge">
            <Sparkles size={12} /> {t.hero.badge}
          </div>
          <h1 className="ynx-hero-title">
            {t.hero.title1}<br />
            <span className="ynx-amber">{t.hero.title2}</span>
          </h1>
          <p className="ynx-hero-sub">
            {t.hero.sub}<br />
            {t.hero.sub2}
          </p>
          <div className="ynx-hero-cta">
            <button className="ynx-btn-primary lg" onClick={() => trackedStart("hero")} data-testid="landing-hero-cta">
              {t.hero.ctaPrimary} <ArrowRight size={16} />
            </button>
            <button className="ynx-btn-ghost lg" onClick={() => trackedJoin("hero")} data-testid="landing-hero-join">
              <Building2 size={14} /> {t.hero.ctaSecondary}
            </button>
          </div>
          <div className="ynx-hero-trust">
            <span><Check size={12} /> {t.hero.trust1}</span>
            <span><Check size={12} /> {t.hero.trust2}</span>
            <span><Check size={12} /> {t.hero.trust3}</span>
          </div>
        </div>
      </header>

      {/* ─── PROOF ─── */}
      <section className="ynx-proof">
        <div className="ynx-proof-inner">
          <div className="ynx-proof-stat">
            <div className="ynx-proof-num">9</div>
            <div className="ynx-proof-lbl">{t.proof.modules}</div>
          </div>
          <div className="ynx-proof-stat">
            <div className="ynx-proof-num">100%</div>
            <div className="ynx-proof-lbl">{t.proof.compliance}</div>
          </div>
          <div className="ynx-proof-stat">
            <div className="ynx-proof-num">&lt;60s</div>
            <div className="ynx-proof-lbl">{t.proof.setup}</div>
          </div>
          <div className="ynx-proof-stat">
            <div className="ynx-proof-num">∞</div>
            <div className="ynx-proof-lbl">{t.proof.unlimited}</div>
          </div>
        </div>
      </section>

      {/* ─── SHOWCASE — official YNX IA Business flyer ─── */}
      <section className="ynx-showcase" data-testid="landing-showcase">
        <div className="ynx-showcase-glow"></div>
        <div className="ynx-showcase-inner">
          <img
            src={`${process.env.PUBLIC_URL || ''}/assets/ynx-flyer.jpg`}
            alt="YNX IA Business — Gestion XXL d'entreprise"
            className="ynx-showcase-img"
            loading="eager"
            decoding="async"
            fetchPriority="high"
            width="1200"
            height="1590"
          />
        </div>
      </section>

      {/* ─── FEATURES ─── */}
      <section className="ynx-section" id="features">
        <div className="ynx-section-inner">
          <div className="ynx-section-head">
            <span className="ynx-section-tag">{t.features.tag}</span>
            <h2 className="ynx-section-title">{t.features.title1}<br/>{t.features.title2}</h2>
            <p className="ynx-section-sub">{t.features.sub}</p>
          </div>
          <div className="ynx-features-grid">
            {t.features.items.map((f, idx) => {
              const Icon = FEATURE_ICONS[idx] || ShieldCheck;
              return (
                <div key={`feat-${idx}`} className="ynx-feature-card" style={{ animationDelay: `${idx * 60}ms` }}>
                  <div className="ynx-feature-icon"><Icon size={20} /></div>
                  <h3 className="ynx-feature-title">{f.title}</h3>
                  <p className="ynx-feature-desc">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── PRICING ─── */}
      <section className="ynx-section ynx-section-dark" id="pricing">
        <div className="ynx-section-inner">
          <div className="ynx-section-head">
            <span className="ynx-section-tag">{t.pricing.tag}</span>
            <h2 className="ynx-section-title">{t.pricing.title1}<br/>{t.pricing.title2}</h2>
            <p className="ynx-section-sub">{t.pricing.sub}</p>
          </div>
          <div className="ynx-pricing-grid">
            {t.pricing.plans.map((p, idx) => (
              <div
                key={`plan-${idx}`}
                className={`ynx-pricing-card ${p.highlighted ? 'highlighted' : ''}`}
                data-testid={`pricing-plan-${idx}`}
              >
                {p.tag && <div className="ynx-pricing-tag">{p.tag}</div>}
                <h3 className="ynx-pricing-name">{p.name}</h3>
                <p className="ynx-pricing-desc">{p.desc}</p>
                <div className="ynx-pricing-price">
                  <span className="ynx-pricing-amount">{p.price}€</span>
                  <span className="ynx-pricing-period">{p.period}</span>
                </div>
                <ul className="ynx-pricing-features">
                  {p.features.map((f, j) => (
                    <li key={`pf-${idx}-${j}`}><Check size={13} /> {f}</li>
                  ))}
                </ul>
                <button
                  className={p.highlighted ? 'ynx-btn-primary block' : 'ynx-btn-ghost block'}
                  onClick={() => trackedStart(`pricing-plan-${idx}`)}
                  data-testid={`pricing-cta-${idx}`}
                >
                  {p.cta}
                </button>
              </div>
            ))}
          </div>

          {/* Add-ons */}
          <div className="ynx-addons-head">
            <h3 className="ynx-addons-title">{t.pricing.addonsTitle}</h3>
            <p className="ynx-addons-sub">{t.pricing.addonsSub}</p>
          </div>
          <div className="ynx-addons-grid">
            {t.pricing.addons.map((a, idx) => (
              <div key={`addon-${idx}`} className="ynx-addon-card" data-testid={`addon-${idx}`}>
                <div className="ynx-addon-plus"><Plus size={14} /></div>
                <div>
                  <div className="ynx-addon-title">{a.title}</div>
                  <div className="ynx-addon-desc">{a.desc}</div>
                </div>
                <div className="ynx-addon-price">+200€<small>/mo</small></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── WHY ─── */}
      <section className="ynx-section">
        <div className="ynx-section-inner">
          <div className="ynx-why-grid">
            <div className="ynx-why-left">
              <span className="ynx-section-tag">{t.why.tag}</span>
              <h2 className="ynx-section-title left">{t.why.title1}<br/>{t.why.title2}</h2>
              <p className="ynx-why-text">{t.why.text}</p>
              <div className="ynx-why-points">
                <div className="ynx-why-point">
                  <Zap size={16} /><span><b>{t.why.p1[0]}</b> {t.why.p1[1]}</span>
                </div>
                <div className="ynx-why-point">
                  <ShieldCheck size={16} /><span><b>{t.why.p2[0]}</b> {t.why.p2[1]}</span>
                </div>
                <div className="ynx-why-point">
                  <Globe size={16} /><span><b>{t.why.p3[0]}</b> {t.why.p3[1]}</span>
                </div>
              </div>
            </div>
            <div className="ynx-why-right">
              <div className="ynx-mockup">
                <div className="ynx-mockup-bar">
                  <span></span><span></span><span></span>
                  <span className="ynx-mockup-url">ynxcloner.com / business</span>
                </div>
                <div className="ynx-mockup-screen">
                  <div className="ynx-mockup-side">
                    <div className="ynx-mockup-side-item active">📅 {t.features.items[0].title}</div>
                    <div className="ynx-mockup-side-item">📋 {t.features.items[1].title}</div>
                    <div className="ynx-mockup-side-item">🧾 {t.features.items[2].title}</div>
                    <div className="ynx-mockup-side-item">👥 {t.features.items[3].title}</div>
                  </div>
                  <div className="ynx-mockup-content">
                    <div className="ynx-mockup-card">
                      <div className="ynx-mockup-card-title">TACITA Conseil — 14:00</div>
                      <div className="ynx-mockup-card-time">Paris 9e</div>
                    </div>
                    <div className="ynx-mockup-card">
                      <div className="ynx-mockup-card-title">FAC-2026-0042</div>
                      <div className="ynx-mockup-card-time"><b>1 280 €</b> · ✓</div>
                    </div>
                    <div className="ynx-mockup-card">
                      <div className="ynx-mockup-card-title">Relancer Dupont SARL</div>
                      <div className="ynx-mockup-card-time">⏰ Today</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section className="ynx-section ynx-section-dark" id="faq">
        <div className="ynx-section-inner narrow">
          <div className="ynx-section-head">
            <span className="ynx-section-tag">{t.faq.tag}</span>
            <h2 className="ynx-section-title">{t.faq.title}</h2>
          </div>
          <div className="ynx-faq-list">
            {t.faq.items.map(([q, a], i) => (
              <div
                key={`faq-${i}`}
                className={`ynx-faq-item ${openFaq === i ? 'open' : ''}`}
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                data-testid={`faq-${i}`}
              >
                <div className="ynx-faq-q">
                  <span>{q}</span>
                  <ChevronDown size={16} className="ynx-faq-chev" />
                </div>
                <div className="ynx-faq-a">{a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FINAL CTA ─── */}
      <section className="ynx-final-cta">
        <div className="ynx-section-inner">
          <h2 className="ynx-final-title">{t.finalCta.title}</h2>
          <p className="ynx-final-sub">{t.finalCta.sub}</p>
          <div className="ynx-hero-cta center">
            <button className="ynx-btn-primary lg" onClick={() => trackedStart("final")} data-testid="landing-final-cta">
              {t.finalCta.cta} <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="ynx-footer">
        <div className="ynx-footer-inner">
          <div>
            <div className="ynx-footer-brand">YNX</div>
            <div className="ynx-footer-sub">{t.footer.tag}<br/>by Carl TACITA · 2026</div>
          </div>
          <div className="ynx-footer-col">
            <h4>{t.footer.product}</h4>
            <a href="#features">{t.nav.features}</a>
            <a href="#pricing">{t.nav.pricing}</a>
            <a href="#faq">{t.nav.faq}</a>
          </div>
          <div className="ynx-footer-col">
            <h4>{t.footer.legal}</h4>
            <a href="mailto:carl@tacita.io">{t.footer.legalMentions}</a>
            <a href="mailto:carl@tacita.io">{t.footer.cgu}</a>
            <a href="mailto:carl@tacita.io">{t.footer.rgpd}</a>
          </div>
          <div className="ynx-footer-col">
            <h4>{t.footer.contact}</h4>
            <a href="mailto:carl@tacita.io">carl@tacita.io</a>
            <button className="ynx-footer-link" onClick={() => trackedStart("footer")}>{t.nav.start}</button>
            <button className="ynx-footer-link" onClick={() => trackedJoin("footer")}>{t.nav.login}</button>
          </div>
        </div>
        <div className="ynx-footer-bottom">
          {t.footer.copyright}
        </div>
      </footer>

      {/* ─── Cookie consent (RGPD) ─── */}
      <ConsentBanner lang={lang} />
    </div>
  );
};
